import React, { useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Divider,
    Button,
    Tabs,
    Tab,
} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';

// Hero Image
import heroImage from '../assets/exhibits/heroImage.JPG';

// --- Depot tour video now hosted on Mux (see playback URL below) ---

// Hollow Stump / Cross Plains / Piedmont Room
import mainExhibit from '../assets/exhibits/Hollow Stump Room/mainExhibit.jpg';

// Southern & Seaboard Railroad Room
import fullRoom from '../assets/exhibits/Cross Plains Room/fullRoom.jpg';

// Cargo Room Images
import visitorMap from '../assets/exhibits/Cargo Room/visitorMap.jpg';
import songsOfAlabama from '../assets/exhibits/Cargo Room/songsOfAlabama.jpg';
import topoMap from '../assets/exhibits/Cargo Room/topoMap.jpg';
import makingAlabama from '../assets/exhibits/Cargo Room/makingAlabama3.jpg';
import nativeAmerican from '../assets/exhibits/Cargo Room/nativeAmerican.jpg';
import textile from '../assets/exhibits/Cargo Room/textile.jpg';

// Piedmont History Room #4 Images
import dentistChair from '../assets/exhibits/Misc/dentist.JPG';
import barberTools from '../assets/exhibits/Misc/barber.jpg';

/* ---------------------------------- */
/*  Custom SVG tab icons (no images)  */
/* ---------------------------------- */

// Rail tracks: two rails with cross ties (not a train)
function RailTracksIcon(props) {
    return (
        <SvgIcon {...props} viewBox="0 0 24 24">
            <path d="M8 3v18M16 3v18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <path d="M4 6h16M4 9h16M4 12h16M4 15h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </SvgIcon>
    );
}

// Crate/box for Cargo Room
function CrateIcon(props) {
    return (
        <SvgIcon {...props} viewBox="0 0 24 24">
            <path d="M4 5h16v14H4z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M4 9h16" fill="none" stroke="currentColor" strokeWidth="1.7" />
            <path d="M4 5l16 14M20 5L4 19" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".9" />
        </SvgIcon>
    );
}

/* ---------------------------------- */
/*  Shared styles + building blocks   */
/* ---------------------------------- */

// One dial to control the desktop width of every exhibit photo
const PHOTO_WIDTH_MD = 420;

const POSTER_IMG_SX = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
};

// Used for all side‑by‑side photos (prevents the "viewport‑sized" bug)
const TWO_COL_IMG_SX = {
    position: 'relative', // container for absolute image
    width: { xs: '100%', md: PHOTO_WIDTH_MD },
    aspectRatio: '4 / 3',
    borderRadius: { xs: 1.5, md: 2 },
    overflow: 'hidden',
    boxShadow: 3,
};

const TEXT_BOX_SX = { flex: 1, maxWidth: { md: '62ch' } };

/** Big feature block (image beside text) */
const FeatureBlock = ({ id, title, img, reverse = false, children }) => (
    <Box component="section" id={id} aria-labelledby={`${id}-title`} sx={{ mb: 8, scrollMarginTop: 96 }}>
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
                alignItems: 'center',
                gap: { xs: 3, sm: 4 },
            }}
        >
            <Box sx={TWO_COL_IMG_SX}>
                <Box component="img" src={img} alt={title} loading="lazy" sx={POSTER_IMG_SX} />
            </Box>

            <Box sx={TEXT_BOX_SX}>
                <Typography
                    id={`${id}-title`}
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        mb: 1,
                        fontSize: { xs: '1.6rem', sm: '1.8rem', md: undefined },
                        lineHeight: { xs: 1.2, sm: 1.25, md: undefined },
                    }}
                >
                    {title}
                </Typography>
                {children}
            </Box>
        </Box>
    </Box>
);

/** Alternating row used for Cargo Room and Room #4 items */
const AltRow = ({ id, title, img, reverse = false, children }) => (
    <Box
        id={id}
        component="article"
        aria-labelledby={`${id}-title`}
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
            alignItems: 'center',
            gap: { xs: 2.5, sm: 3 },
            py: 3,
            scrollMarginTop: 96,
        }}
    >
        <Box sx={TWO_COL_IMG_SX}>
            <Box component="img" src={img} alt={title} loading="lazy" sx={POSTER_IMG_SX} />
        </Box>

        <Box sx={TEXT_BOX_SX}>
            <Typography
                id={`${id}-title`}
                variant="h5"
                sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.15rem', sm: '1.25rem', md: undefined },
                    lineHeight: { xs: 1.35, sm: 1.35, md: undefined },
                }}
            >
                {title}
            </Typography>
            <Typography variant="body1" color="text.secondary">{children}</Typography>
        </Box>
    </Box>
);

export default function Exhibits() {
    const tabs = useMemo(
        () => [
            { value: 'railroad', label: 'Railroad Room', anchor: 'railroad-room', icon: <RailTracksIcon /> },
            { value: 'piedmont', label: 'Piedmont Room', anchor: 'hollow-stump-piedmont', icon: <StorefrontRoundedIcon /> },
            { value: 'cargo', label: 'Cargo Room', anchor: 'cargo-room', icon: <CrateIcon /> },
            { value: 'room4', label: 'History Room #4', anchor: 'piedmont-history-room-4', icon: <CollectionsBookmarkIcon /> },
        ],
        []
    );

    // Default to Railroad tab
    const [tab, setTab] = useState('railroad');

    const handleTabChange = (_e, value) => {
        setTab(value);
        const target = tabs.find(t => t.value === value)?.anchor;
        if (target) {
            const el = document.getElementById(target);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <Box id="page-top">
            {/* Hero */}
            <Box
                sx={{
                    height: { xs: 220, md: 300 },
                    backgroundImage: `url(${heroImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        textAlign: 'center',
                        px: 2,
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            letterSpacing: 0.2,
                            fontSize: { xs: '1.9rem', sm: '2.2rem', md: undefined },
                            lineHeight: { xs: 1.2, sm: 1.25, md: undefined },
                        }}
                    >
                        Every Exhibit Tells a Story
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            opacity: 0.95,
                            fontSize: { xs: '1rem', sm: '1.05rem', md: undefined },
                            lineHeight: { xs: 1.45, sm: 1.45, md: undefined },
                            maxWidth: { sm: '70ch' },
                        }}
                    >
                        Our museum exhibits show the history of Piedmont and Alabama.
                    </Typography>
                </Box>
            </Box>

            {/* --- NEW: Depot Tour Video (placed near the very top) --- */}
            <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 } }}>
                <Typography
                    component="h1"
                    variant="h4"
                    align="center"
                    sx={{ fontWeight: 800, mb: 1, letterSpacing: 0.2 }}
                >
                    Take a video tour of the depot!
                </Typography>
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '16 / 9',
                        borderRadius: { xs: 1.5, md: 2 },
                        overflow: 'hidden',
                        boxShadow: 4,
                        backgroundColor: '#000',
                    }}
                >
                    <Box
                        component="iframe"
                        src="https://player.mux.com/YKTo01c7ByxD8IkzKOov6x5xbe02yUGToAmRtx02KpsUuE"
                        title="Take a video tour of the depot"
                        allowFullScreen
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                        sx={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                    />
                </Box>
            </Container>

            {/* Room Navigator — sticky on tablet/desktop only */}
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: { xs: 'static', sm: 'sticky' },
                    top: { sm: 0 },
                    zIndex: { sm: 10 },
                }}
            >
                <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        allowScrollButtonsMobile
                        aria-label="Room navigator"
                        sx={{
                            minHeight: { xs: 54, md: 62 },
                            '& .MuiTab-root': { minHeight: { xs: 54, md: 62 } },
                        }}
                    >
                        {tabs.map(t => (
                            <Tab
                                key={t.value}
                                value={t.value}
                                icon={t.icon}
                                iconPosition="start"
                                label={t.label}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1rem', md: '1.05rem' },
                                    px: { xs: 1.25, sm: 2 },
                                }}
                            />
                        ))}
                    </Tabs>
                </Container>
            </Box>

            {/* Page content */}
            <Box sx={{ backgroundColor: '#fff', pt: 8, pb: 8 }}>
                <Container maxWidth="md">
                    {/* Railroad Room FIRST */}
                    <FeatureBlock id="railroad-room" title="Southern & Seaboard Railroad Room" img={fullRoom} reverse>
                        <Typography paragraph>
                            Before the rails, travel through Cross Plains followed footpaths and rough roads. In 1868 the Southern and
                            Seaboard Railroad brought regular mail, people, and freight. The depot turned a quiet crossroads into a working town.
                        </Typography>
                        <Typography paragraph>Highlights:</Typography>
                        <Box component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
                            <li>A wall map showing nearby depots and historic views of each station.</li>
                            <li>Original Southern and Seaboard artifacts: spikes, freight logs, station signs, and tools.</li>
                            <li>Photographs, maps, and records that follow daily life along the rails.</li>
                        </Box>
                    </FeatureBlock>

                    <Divider sx={{ my: 6 }} />

                    {/* Piedmont Room SECOND */}
                    <FeatureBlock id="hollow-stump-piedmont" title="Hollow Stump / Cross Plains / Piedmont Room" img={mainExhibit}>
                        <Typography paragraph>
                            Before there was a post office, a large hollow sycamore stump on the edge of town served as the mailbox for Cross
                            Plains, now Piedmont. People left letters inside, and neighbors carried them as they traveled.
                        </Typography>
                        <Typography paragraph>
                            This room pulls together early Piedmont: everyday tools, family keepsakes, and photographs. A slide show labels
                            buildings, people, and events.
                        </Typography>
                        <Typography paragraph>
                            Around the life-size stump replica, photos trace how downtown changed from dirt roads to brick streets and
                            storefronts.
                        </Typography>
                    </FeatureBlock>

                    <Divider sx={{ my: 6 }} />

                    {/* Cargo Room */}
                    <Box component="section" id="cargo-room" aria-labelledby="cargo-room-title" sx={{ mb: 2, scrollMarginTop: 96 }}>
                        <Typography
                            id="cargo-room-title"
                            variant="h3"
                            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', sm: '1.8rem', md: undefined } }}
                        >
                            Cargo Room
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: { md: '70ch' } }}>
                            These stops cover maps, music, artifacts, and hands-on displays that connect Alabama's landscape and culture.
                        </Typography>

                        <AltRow id="cargo-room-overview" title="Sweet Home Alabama" img={visitorMap}>
                            The Cargo Room brings together exhibits about Alabama, including geography,
                            music, archaeology, and industry. The maps here allow visitors to place pins of where they are from!
                        </AltRow>

                        <AltRow id="songs-of-alabama" title="Songs of Alabama" img={songsOfAlabama} reverse>
                            Browse more than eighty recordings that mention Alabama, from folk to swing. Five listening stations make it easy to
                            enjoy with friends, and the full playlist can run as background music during events at the depot.
                        </AltRow>

                        <AltRow id="topo-projection" title="Sweet Home Alabama Topography Projection" img={topoMap}>
                            Our projection features the <em>Sweet Home Alabama</em> presentation, an animated tour that moves from the state's
                            geography to its politics and people. Watch how rivers, ridges, and routes shaped communities, travel, and work.
                        </AltRow>

                        <AltRow id="making-alabama" title="Making Alabama" img={makingAlabama} reverse>
                            Timelines, photos, and objects trace the state's story from early settlements to today. Quick stops highlight turning
                            points such as statehood, industry, and civil rights, and connect them to local names.
                        </AltRow>

                        <AltRow id="native-artifacts" title="Native American Artifacts" img={nativeAmerican}>
                            Stone tools, arrowheads, and pottery linked to Creek and Cherokee homelands in this region. Labels focus on everyday work
                            such as hunting, fishing, and farming, and the craftsmanship behind each piece.
                        </AltRow>

                        <AltRow id="textile-exhibit" title="Textile Exhibit" img={textile} reverse>
                            Ever wonder how cotton and mills shaped Piedmont's economy? Tools, records, and mill items show the routines of work and the people
                            who kept it going.
                        </AltRow>
                    </Box>

                    <Divider sx={{ my: 6 }} />

                    {/* Piedmont History Room #4 */}
                    <Box component="section" id="piedmont-history-room-4" aria-labelledby="room-4-title" sx={{ scrollMarginTop: 96 }}>
                        <Typography
                            id="room-4-title"
                            variant="h3"
                            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', sm: '1.8rem', md: undefined } }}
                        >
                            Piedmont History Room #4
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: { md: '70ch' } }}>
                            This room shows elements of history in Piedmont! From old dentist equipment, barber shop objects, to old high school uniforms and yearbooks, you get a sense of Piedmont pride and a long history of community in our town.
                        </Typography>

                        <AltRow id="room4-dentist" title="Dentist’s Chair" img={dentistChair}>
                            The dentist display comes from old dentistry tools left behind by one of Piedmont's former dentists. These artifacts trace back to the 1940s!
                        </AltRow>

                        <AltRow id="room4-barber" title="Barber’s Tools" img={barberTools} reverse>
                            Check out the display showing old-timey objects to get a feel of what it was like to visit a barber shop back in the day!
                        </AltRow>
                    </Box>

                    {/* Back to top */}
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Button
                            variant="outlined"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            sx={{ borderRadius: 9999, px: 4 }}
                            aria-label="Back to the top"
                        >
                            Back to the top
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
